/* eslint-disable @typescript-eslint/no-explicit-any */
import { Color, Layer as DeckGLLayer } from '@deck.gl/core'
import { CollisionFilterExtension } from '@deck.gl/extensions'
import { GeoJsonLayer, GeoJsonLayerProps } from '@deck.gl/layers'
import { SpatialFilter, TilejsonResult, WidgetSource } from '@carto/api-client'
import { Layer, LAYER_VIS_CONFIGS, UpdateTriggers } from '@carto/kepler.gl/layers'
import { SCALE_TYPES, UNKNOWN_COLOR, AGGREGATION_TYPES, CHANNEL_SCALES } from '@carto/kepler.gl/constants'
import { checkColorOrFallback, notNullorUndefined, negateAccessor } from '@carto/kepler.gl/utils'
import { PointLabelLayer, VectorTileLayer } from '@deck.gl/carto'
import { getResolution as getQuadbinResolution } from 'quadbin'
import { getResolution as getH3Resolution } from 'h3-js'
import { TILE_FORMATS } from '@carto/react-core'
import {
  getAttributesFromTilesetDataset,
  getBoundsFromTilesetDataset,
  getGeometryFromTilesetDataset,
  getDatasetType,
  attributeHasStats,
  getSchemeFromTilesetDataset,
  getTilejsonFromTilesetDataset,
  getSpatialIndexFromGeoColumn,
  getHexColumnStatsFromTilesetDataset
} from 'features/builder/state/builderSliceUtils'
import {
  LAYER_HIGHLIGHT_FILL_COLOR_HOVER,
  LAYER_HIGHLIGHT_FILL_COLOR_PRIMARY,
  LAYER_HIGHLIGHT_FILL_COLOR_SECONDARY,
  LAYER_HIGHLIGHT_LINE_COLOR_PRIMARY,
  LAYER_HIGHLIGHT_LINE_WIDTH_MEDIUM,
  LAYER_HIGHLIGHT_LINE_WIDTH_HEAVY,
  LAYER_HIGHLIGHT_LINE_WIDTH_LIGHT
} from './constants'
import {
  CALCULATE_FEATURES_DEBOUNCE_TIME,
  DEFAULT_UNIQUE_ID_PROPERTY,
  TILESETS_UNIQUE_ID_PROPERTY
} from 'features/builder/ui/builderConstants'
import IconTilesets from 'assets/icons/layers/tilesets.svg?react'
import { GeomType, MapType } from 'features/maps/types'
import { calculateFeaturesForTilesetRequest, setTilesetWidgetSourceReady } from 'features/builder/state/builderSlice'
import { getStore } from 'app/state/storeRegistry'
import { setIsLayerLoadingTiles } from 'features/builder/state/keplerGlActions'
import { getToken } from 'features/common/api/requests'
import { getLegendSettings } from 'features/builder/api/builderQueries'
import { getColumnAliasForAggregationExp } from 'features/builder/state/spatialIndexUtils'
import { getDebounceTime, getDynamicTileResolution, getTileSize } from 'features/common/utils/cartoDeckGL'
import { cartoVectorTileLoadOptions } from 'features/common/utils/deckGlWorkers'
import {
  GeometryType,
  LayerTypes,
  LayerType,
  KeplerColorRange,
  KeplerLayer,
  KeplerLayerConfig,
  KeplerLayerMeta,
  KeplerTable,
  KeplerTableField,
  KeplerVisualChannel,
  SpatialIndex,
  KeplerScaleType
} from 'features/builder/types'
import { createNewLayerColorGenerator } from './colorUtils'
import { colorMapsEqual, getLog10ScaleColorMap } from '../../layers/colorScaleUtils'

const collisionFilterExtension = new CollisionFilterExtension()

const noneLayerDataAffectingProps = ['label', 'opacity', 'thickness', 'isVisible', 'hidden', 'isConfigActive']
const noneLayerDataAffectingPropsWithOpacityForChannel = noneLayerDataAffectingProps.filter(
  (prop) => prop !== 'opacity'
)

export const tilesetVisConfigs = {
  opacity: 'opacity',
  strokeOpacity: {
    ...LAYER_VIS_CONFIGS.opacity,
    property: 'strokeOpacity'
  },
  thickness: {
    ...LAYER_VIS_CONFIGS.thickness,
    defaultValue: 1
  },
  strokeColor: 'strokeColor',
  colorRange: 'colorRange',
  strokeColorRange: 'strokeColorRange',
  radius: 'radius',
  sizeRange: 'strokeWidthRange',
  radiusRange: {
    ...LAYER_VIS_CONFIGS.radiusRange,
    range: [0, 100]
  },
  customMarkerSize: {
    ...LAYER_VIS_CONFIGS.radius,
    range: [0, 200]
  },
  customMarkerSizeRange: {
    ...LAYER_VIS_CONFIGS.radiusRange,
    range: [0, 200]
  },
  heightRange: 'elevationRange',
  elevationScale: 'elevationScale',
  stroked: 'stroked',
  filled: 'filled',
  customMarkers: 'customMarkers',
  customMarkersUrl: 'customMarkersUrl',
  customMarkersId: {
    type: 'string',
    defaultValue: undefined
  },
  customMarkersRange: 'customMarkersRange',
  enable3d: 'enable3d',
  wireframe: 'wireframe',
  initialStrokeColor: {
    type: 'color',
    defaultValue: null
  },
  colorAggregation: 'colorAggregation',
  strokeColorAggregation: {
    type: 'string',
    defaultValue: null
  },
  radiusAggregation: {
    type: 'string',
    defaultValue: null
  },
  sizeAggregation: {
    type: 'string',
    defaultValue: null
  }
}

export const defaultElevation = 500
export const defaultLineWidth = 1
export const defaultRadius = 1

const textStyleProps = {
  textCharacterSet: 'auto',
  textFontFamily: 'Inter, sans',
  textFontSettings: { sdf: true },
  textFontWeight: 500,
  textOutlineWidth: 4
}

const strokedGeometries = [
  GeometryType.Polygon,
  GeometryType.MultiPolygon,
  GeometryType.Line,
  GeometryType.LineString,
  GeometryType.MultiLineString
]

function getTextAccessor({ name }) {
  return (d, info) => {
    const { index, data } = info
    const { properties, numericProps } = data
    const val = properties[index][name] || numericProps[name]?.value[index]
    return notNullorUndefined(val) ? String(val) : ''
  }
}

export function opacityToAlpha(opacity?: number) {
  return opacity !== undefined ? Math.round(255 * Math.pow(opacity, 1 / 2.2)) : 255
}

function getColorWithOpacity(color: number[], opacity = 1) {
  return [color[0], color[1], color[2], opacityToAlpha(opacity)]
}

function getFirstContentIdFromTiles(tiles): bigint | string | null {
  for (const tile of tiles) {
    if (tile.content && tile.content.length > 0) {
      return tile.content[0].id
    }
  }
  return null
}

const numericFieldTypes = ['number', 'integer', 'real', 'float']
export default class TileLayer extends Layer {
  [x: string]: any
  static type: string
  spatialIndexResolution?: number
  declare meta: KeplerLayerMeta
  // @ts-expect-error TODO: Resolve type differences.
  declare config: KeplerLayerConfig

  constructor(props = {}) {
    super(props)
    this.registerVisConfig(tilesetVisConfigs)
  }

  public get type(): LayerType {
    return LayerTypes.Tileset
  }

  get name() {
    return 'Tiles'
  }

  get layerIcon() {
    return IconTilesets
  }

  get canDropFeatures() {
    return true
  }

  /**
   * Use opacity per channel for geometries that are rendered in single draw call, sharing one opacity value.
   *
   * Needed for layers like ScatterPlotLayer which has only "whole layer" opacity setting. We workaround it by using RGBA in
   * color accessor for stroke and fill to use color with proper, per channel opacity.
   * Also needed for SpatialIndexLayer which is rendered using instancing also in one rener pass.
   *
   * Polygon/line are using two render passes, each with specific opacity, so we don't need this workaround there.
   */
  get useOpacityPerChannel() {
    return this.meta.featureTypes?.point
  }

  getChannelColorValue(colorValue, opacityValue) {
    if (this.useOpacityPerChannel) {
      return getColorWithOpacity(colorValue, opacityValue)
    } else {
      return colorValue
    }
  }

  get visualChannels(): any {
    const visualChannels = super.visualChannels
    return {
      color: {
        ...visualChannels.color,
        opacityProperty: 'opacity',
        accessor: 'getFillColor',
        condition: (config: any) => config.visConfig.filled,
        nullValue: visualChannels.color.nullValue,
        // defaultValue must be static.
        getAttributeValue: (config: any) => this.getChannelColorValue(config.color, config.visConfig.opacity),
        defaultValue: (config: any) => config.color,
        aggregation: 'colorAggregation'
      },
      strokeColor: {
        property: 'strokeColor',
        opacityProperty: 'strokeOpacity',
        field: 'strokeColorField',
        scale: 'strokeColorScale',
        domain: 'strokeColorDomain',
        range: 'strokeColorRange',
        key: 'strokeColor',
        channelScaleType: CHANNEL_SCALES.color,
        accessor: 'getLineColor',
        condition: (config: any) => config.visConfig.stroked,
        nullValue: visualChannels.color.nullValue,
        // defaultValue must be static.
        getAttributeValue: (config: any) =>
          this.getChannelColorValue(
            config.visConfig.strokeColor || config.color,
            config.visConfig.strokeOpacity ?? config.visConfig.opacity
          ),
        defaultValue: (config: any) => config.visConfig.strokeColor || config.color,
        aggregation: 'strokeColorAggregation'
      },
      size: {
        ...visualChannels.size,
        property: 'stroke',
        accessor: 'getLineWidth',
        condition: (config: any) => config.visConfig.stroked,
        nullValue: 0,
        getAttributeValue: () => (d: any) => d.properties.lineWidth || defaultLineWidth,
        defaultValue: () => defaultLineWidth,
        supportedFieldTypes: numericFieldTypes,
        aggregation: 'sizeAggregation'
      },
      height: {
        property: 'height',
        field: 'heightField',
        scale: 'heightScale',
        domain: 'heightDomain',
        range: 'heightRange',
        key: 'height',
        channelScaleType: CHANNEL_SCALES.size,
        accessor: 'getElevation',
        condition: (config: any) => config.visConfig.enable3d,
        nullValue: 0,
        getAttributeValue: () => (d: any) => d.properties?.elevation || defaultElevation,
        supportedFieldTypes: numericFieldTypes
      },
      radius: {
        property: 'radius',
        field: 'radiusField',
        scale: 'radiusScale',
        domain: 'radiusDomain',
        range: 'radiusRange',
        key: 'radius',
        channelScaleType: CHANNEL_SCALES.radius,
        accessor: 'getPointRadius',
        supportedFieldTypes: numericFieldTypes,
        nullValue: 0,
        getAttributeValue: () => (d: any) => d.properties.radius || defaultRadius,
        aggregation: 'radiusAggregation'
      },
      customMarkers: {
        property: 'customMarkers',
        key: 'customMarkers',
        field: 'customMarkersField',
        scale: 'customMarkersScale',
        domain: 'customMarkersDomain',
        range: 'customMarkersRange',
        fixed: 'customMarkersUrl',
        channelScaleType: CHANNEL_SCALES.customMarker,
        accessor: 'getIconUrl',
        supportedFieldTypes: ['string'],
        getAttributeValue: (config) => config.visConfig.customMarkersUrl,
        condition: (config) => config.visConfig.customMarkers
      },
      rotation: {
        property: 'rotation',
        key: 'rotation',
        field: 'rotationField',
        domain: 'rotationDomain',
        scale: 'rotationScale',
        channelScaleType: CHANNEL_SCALES.identity,
        accessor: 'getRotation',
        supportedFieldTypes: numericFieldTypes,
        defaultValue: () => 0,
        nullValue: 0,
        condition: (config) => config.rotationField
      }
    }
  }

  static findDefaultLayerProps(dataset: KeplerTable, foundLayers) {
    const emptyProps = { props: [] }

    if (foundLayers?.length) {
      return emptyProps
    }

    const scheme = getSchemeFromTilesetDataset(dataset)

    if (getDatasetType(dataset) === MapType.tileset && (!scheme || scheme === 'xyz')) {
      return {
        props: [
          {
            label: dataset.label,
            isVisible: true,
            columns: {}
          }
        ]
      }
    }
    return emptyProps
  }

  getDefaultLayerConfig(props = {}): any {
    const baseConfig = super.getDefaultLayerConfig(props)
    return {
      ...baseConfig,
      initialLayerColor: baseConfig.color,
      // add height visual channel
      heightField: null,
      heightDomain: [0, 1],
      heightScale: 'linear',

      // add radius visual channel
      radiusField: null,
      radiusDomain: [0, 1],
      radiusScale: 'linear',

      // add stroke color visual channel
      strokeColorField: null,
      strokeColorDomain: [0, 1],
      strokeColorScale: 'quantile',

      // add highlight color
      highlightColor: [...LAYER_HIGHLIGHT_FILL_COLOR_HOVER]
    }
  }

  /**
   * Used to copy config from old layer, when converting layer type.
   *
   * @param configToCopy old layer instance config
   */
  assignConfigToLayer(configToCopy: KeplerLayerConfig) {
    const {
      dataId,
      color,
      columns,
      animation,
      isVisible,
      label,
      isConfigActive,
      visConfig: oldVisConfig
    } = configToCopy
    const { filled = true } = oldVisConfig
    const visConfig = { ...this.config.visConfig, filled }
    this.updateLayerConfig({
      dataId,
      color,
      columns,
      animation,
      isVisible,
      label,
      isConfigActive,
      visConfig
    })
    Object.keys(this.visualChannels).forEach((channel) => {
      this.validateVisualChannel(channel)
    })
  }

  getHoverData(object: any) {
    if (!object) {
      return null
    }

    return object.properties
  }

  neededAttributesAreAvailable(dataset: KeplerTable) {
    const attributes = getAttributesFromTilesetDataset(dataset)

    return Object.keys(this.visualChannels).every((channel) => {
      const { field: fieldName, channelScaleType } = this.visualChannels[channel]
      if (channelScaleType === 'identity') return true

      const field = this.config[fieldName]
      if (!field) return true

      const attribute = attributes.find((attr) => attr.attribute === field.id)
      return attributeHasStats(attribute)
    })
  }

  get noneLayerDataAffectingProps() {
    return this.useOpacityPerChannel ? noneLayerDataAffectingPropsWithOpacityForChannel : noneLayerDataAffectingProps
  }

  formatLayerData(datasets: any, oldLayerData: any) {
    const dataset = datasets[this.config.dataId]

    // Dynamic tiling  loads attributes stats on demand so we return the previous layer configuration
    // while the request is resolved if any attribute is necessary to style and is not available yet
    if (!this.neededAttributesAreAvailable(dataset)) {
      return oldLayerData
    }
    const { fields, gpuFilter, dataContainer } = dataset
    const data = getTilejsonFromTilesetDataset(dataset)
    if (!data) {
      return null
    }

    const formatTiles = new URL(data.tiles[0]).searchParams.get('formatTiles') || TILE_FORMATS.MVT

    const bounds = getBoundsFromTilesetDataset(dataset)
    const geometry = getGeometryFromTilesetDataset(dataset)
    const featureTypes = geometry && { [geometry.toLocaleLowerCase()]: true }
    const geoColumn = dataset.metadata?.geoColumn
    const scheme = dataset.metadata?.scheme
    const schema = dataset.metadata?.schema
    const sourceType = dataset.metadata?.sourceType
    const provider = dataset.metadata?.provider

    this.updateMeta({
      ...(featureTypes && { featureTypes }),
      ...(bounds && { bounds }),
      formatTiles,
      geoColumn,
      scheme,
      schema,
      sourceType,
      provider
    })

    const getFilterValue = this.getFilterValue(fields, gpuFilter)
    this.updateHexColorDomain(dataset)
    this.updateColorMaps()

    const accessors = this.getAttributeAccessors({ dataContainer })

    return {
      data,
      Layer,
      getFilterValue,
      ...accessors
    }
  }

  hasLayerData(layerData) {
    return Boolean(layerData)
  }

  getFilterValueAccessor(fields: any) {
    return (_, d: any, fieldIndex: number) => {
      const field = fields.find((f) => f.fieldIdx === fieldIndex)
      if (field) {
        return d.properties[field.name]
      }
    }
  }

  getFilterValue(fields: any, gpuFilter: any) {
    const filterValueAccessor = this.getFilterValueAccessor(fields)
    const gpuFilterFilterValueAccessor = gpuFilter.filterValueAccessor()(null, filterValueAccessor)
    return (d: any) => gpuFilterFilterValueAccessor(d)
  }

  /**
   * This creates config for  _newly_ added layer.
   *
   * In contrast to Layer.getDefaultLayerConfig, which is called several
   * times during the layer lifecycle.
   */
  getNewLayerConfig(dataset: KeplerTable, otherLayers: KeplerLayer[]): Partial<KeplerLayerConfig> {
    const geometry = getGeometryFromTilesetDataset(dataset)
    const colorGenerator = createNewLayerColorGenerator(
      otherLayers.filter((layer) => layer !== (this as unknown as KeplerLayer))
    )
    const color = colorGenerator()
    const strokeColor = colorGenerator()
    const stroked = geometry ? strokedGeometries.includes(geometry) : false

    return {
      color,
      visConfig: {
        ...this.config.visConfig,
        filled: true,
        stroked,
        strokeColor,
        initialStrokeColor: strokeColor
      }
    }
  }

  updateHexColorDomain(dataset: KeplerTable) {
    Object.values(this.visualChannels as Record<string, KeplerVisualChannel>).forEach((channel) => {
      const colorRange: KeplerColorRange | undefined = this.config.visConfig[channel.range]
      const field: KeplerTableField | undefined = this.config[channel.field]

      if (!colorRange?.hexColor || !field?.colorColumn) return

      const attribute = getHexColumnStatsFromTilesetDataset(dataset, field.colorColumn)
      if (attribute && 'categories' in attribute) {
        const colors = attribute?.categories?.map(({ category }) => checkColorOrFallback(category))
        this.updateLayerVisConfig({ [channel.range]: { ...colorRange, colors } })
      }
    })

    return this
  }

  /**
   * Some colorMaps* may be outdated, as they are _selected_ synchronously when changing scale type,
   * but domain is not yet available. This method updates colorMaps for scales that need it.
   *
   * * scale === custom` && `range.uiCustomScaleType` === 'logarithmic'
   */
  updateColorMaps() {
    const visConfig = this.config.visConfig
    let newVisConfig = visConfig

    for (const channel of Object.values(this.visualChannels) as KeplerVisualChannel[]) {
      const scale: KeplerScaleType | undefined = this.config[channel.scale]
      const colorRange: KeplerColorRange | undefined = this.config.visConfig[channel.range]
      const field: KeplerTableField | undefined = this.config[channel.field]
      const domain = this.config[channel.domain]

      if (!scale || !colorRange || !field) continue

      if (scale === KeplerScaleType.Custom && colorRange.uiCustomScaleType === 'logarithmic') {
        const log10ScaleColorMap = getLog10ScaleColorMap({
          min: domain[0],
          max: domain[1],
          colors: colorRange.colors
        })
        if (!colorRange.colorMap || !colorMapsEqual(colorRange.colorMap, log10ScaleColorMap)) {
          newVisConfig = {
            ...newVisConfig,
            [channel.range]: {
              ...colorRange,
              colorMap: log10ScaleColorMap
            }
          }
        }
      }
    }
    if (newVisConfig !== visConfig) {
      this.updateLayerVisConfig(newVisConfig)
    }
  }

  calculateLayerDomain(dataset: any, visualChannel: any) {
    const defaultDomain = [0, 1]
    const { scale, aggregation } = visualChannel
    const scaleType = this.config[scale]
    const aggregationType = aggregation && this.config.visConfig[aggregation]

    const field = this.config[visualChannel.field]
    if (!field) {
      return defaultDomain
    }

    const attribute: any = getAttributesFromTilesetDataset(dataset).find((a: any) => a.attribute === field.name)
    const steps = this.config.visConfig[visualChannel.range]?.colors?.length

    if (aggregationType === AGGREGATION_TYPES.count) {
      return [0, Infinity]
    }
    if (attribute && attributeHasStats(attribute)) {
      switch (scaleType) {
        case SCALE_TYPES.ordinal:
        case SCALE_TYPES.point:
          return attribute.categories
            .map((c: { category: string }) => c.category)
            .filter((c: string) => c !== undefined && c !== null)

        case SCALE_TYPES.quantile:
          return attribute.quantiles[steps]

        case SCALE_TYPES.log: {
          const [d0, d1] = [attribute.min, attribute.max]
          return [d0 === 0 ? 1e-5 : d0, d1]
        }

        case SCALE_TYPES.quantize:
        case SCALE_TYPES.linear:
        case SCALE_TYPES.sqrt:
        default:
          return [attribute.min, attribute.max]
      }
    }

    return defaultDomain
  }

  async calculateHexColumnLegend(datasets: Record<string, KeplerTable>) {
    const legends = {}
    const dataset: KeplerTable | undefined = this.getDataset(datasets)
    if (!dataset || dataset.metadata.sourceType === MapType.tileset) {
      return
    }

    for (const channel of Object.keys(this.visualChannels)) {
      const { range, field: channelField } = this.visualChannels[channel]
      const colorRange: KeplerColorRange | undefined = this.config.visConfig[range]
      const field: KeplerTableField | undefined = this.config[channelField]

      if (colorRange?.hexColor && field?.colorColumn) {
        const response = await getLegendSettings(dataset, field)

        if (response) {
          legends[channel] = response.rows.map((row) => ({ ...row, color: checkColorOrFallback(row.color) }))
        }
      }
    }

    this.updateLayerLegend(legends)
  }

  getEncodedChannelValue({ channel, scale, data, dataContainer, field, aggregation, nullValue }) {
    const value = super.getEncodedChannelValue({
      channel,
      scale,
      data,
      dataContainer,
      field,
      aggregation,
      nullValue,
      getValue: (field: any, data: any) => {
        const fieldValue = data.properties[field.name]
        return notNullorUndefined(fieldValue)
          ? fieldValue
          : data.properties[getColumnAliasForAggregationExp(field.name, aggregation, this.meta.provider)]
      }
    })

    if (value !== nullValue && this.useOpacityPerChannel) {
      if (channel === 'color') {
        return getColorWithOpacity(value, this.config.visConfig.opacity)
      }
      if (channel === 'strokeColor') {
        return getColorWithOpacity(value, this.config.visConfig.strokeOpacity ?? this.config.visConfig.opacity)
      }
    }

    return value
  }

  clearDebounce() {
    clearTimeout(this.debounceId)
  }

  getCustomFetch(tilejson: TilejsonResult) {
    return (url, { layer, loaders, loadOptions, signal }) => {
      const accessToken = tilejson.accessToken
      return this.customFetch(url, { layer, loaders, loadOptions, signal, accessToken })
    }
  }

  private async customFetch(url, { layer, loaders, loadOptions, signal, accessToken }) {
    this.clearDebounce()
    if (!this.isLoadingTiles) {
      const store = getStore()
      store.dispatch(setIsLayerLoadingTiles({ layerId: this.id, isLoadingTiles: true }))
      store.dispatch(setTilesetWidgetSourceReady({ sourceId: this.config.dataId, ready: false }))
    }

    loaders = Array.isArray(loaders) ? [layer.props.loaders, ...loaders] : layer.props.loaders
    if (!accessToken) {
      accessToken = await getToken()
    }
    loadOptions = loadOptions || layer.getLoadOptions() || {}
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : loadOptions.fetch.headers
    loadOptions.fetch = { ...loadOptions.fetch, headers }

    return (DeckGLLayer as any).defaultProps.fetch.value(url, { layer, loaders, loadOptions, signal, headers })
  }

  calculateFeatures() {
    this.clearDebounce()
    this.debounceId = setTimeout(() => {
      getStore().dispatch(calculateFeaturesForTilesetRequest({ layerId: this.id }))
    }, CALCULATE_FEATURES_DEBOUNCE_TIME)
  }

  getVisualChannelUpdateTriggers() {
    const baseTriggers = super.getVisualChannelUpdateTriggers()
    if (this.useOpacityPerChannel) {
      baseTriggers.getFillColor.opacity = this.config.visConfig.opacity
      baseTriggers.getLineColor.opacity = this.config.visConfig.strokeOpacity
    }

    return baseTriggers
  }

  getRenderProps(opts: any) {
    const { data, gpuFilter } = opts
    const { featureTypes, formatTiles, geoColumn, scheme, sourceType } = this.meta

    const radiusScale = this.getRadiusScale()
    const { visConfig } = this.config

    let layerProps: Partial<GeoJsonLayerProps> = {}

    const filterValueUpdateTriggers = gpuFilter.filterValueUpdateTriggers

    const updateTriggers: UpdateTriggers = {
      ...this.getVisualChannelUpdateTriggers(),
      getFilterValue: filterValueUpdateTriggers
    }

    const defaultLayerProps = this.getDefaultDeckLayerProps(opts)
    const opaOverwrite = !this.useOpacityPerChannel
      ? {
          opacity: visConfig.strokeOpacity
        }
      : {}

    const pickable = this.hasInteractivity

    const customMarkersField = (this.config as unknown as KeplerLayerConfig).customMarkersField
    const { customMarkers, customMarkersUrl, customMarkersRange } = this.config.visConfig
    const customMarkersConfigured =
      customMarkers && Boolean(customMarkersUrl || (customMarkersField && customMarkersRange))
    const customMarkersMisConfigured = customMarkers && !customMarkersConfigured

    // TODO: invent better way to render something when customMarkers are not yet configured
    const pointRadiusScale = customMarkersMisConfigured ? radiusScale / 2 : radiusScale
    const maxIconSize = this.getMaxMarkerSize()

    if (featureTypes?.point && customMarkersConfigured) {
      const useMask = Boolean(this.config.visConfig.filled)
      layerProps = {
        pointType: 'icon',
        getIcon: this.getCustomMarkerIconAccessor(data.getIconUrl, useMask),
        getIconSize: data.getPointRadius,
        getIconAngle: negateAccessor(data.getRotation),
        getIconColor: this.config.visConfig.filled ? data.getFillColor : UNKNOWN_COLOR,
        iconSizeScale: radiusScale,
        iconSizeUnits: 'pixels',
        parameters: {
          // circles will be flat on the map when the altitude column is not used
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          depthTest: this.config.columns.altitude?.fieldIdx > -1
        },
        updateTriggers: {
          ...updateTriggers,
          getFilterValue: updateTriggers.getFilterValue,
          getIconColor: { ...updateTriggers.getFillColor, filled: this.config.visConfig.filled },
          getIcon: { ...updateTriggers.getIconUrl, maxIconSize, useMask },
          getIconSize: updateTriggers.getPointRadius,
          getIconAngle: updateTriggers.getRotation
        }
      }
    } else {
      layerProps = {
        pointType: 'circle',
        lineWidthScale: this.config.visConfig.thickness,
        lineWidthUnits: 'pixels',
        elevationScale: visConfig.elevationScale,
        pointRadiusScale,
        pointRadiusUnits: 'pixels',
        lineMiterLimit: 4,
        stroked: visConfig.stroked,
        filled: visConfig.filled,
        extruded: visConfig.enable3d,
        wireframe: visConfig.wireframe,
        updateTriggers
      }
    }

    const { textLabel } = this.config
    const [mainLabel, secondaryLabel] = textLabel
    const {
      alignment: getTextAlignmentBaseline,
      anchor: getTextAnchor,
      color: getTextColor,
      field: mainField,
      outlineColor: textOutlineColor,
      size: textSizeScale
    } = mainLabel || {}
    const {
      color: getSecondaryColor,
      field: secondaryField,
      outlineColor: secondaryOutlineColor,
      size: secondarySizeScale
    } = secondaryLabel || {}
    const getText = mainField && getTextAccessor(mainField)
    const getSecondaryText = secondaryField && getTextAccessor(secondaryField)
    if (getText) {
      layerProps = {
        ...layerProps,
        pointType: `${layerProps.pointType}+text`,

        // Common label props
        getText,
        getTextAlignmentBaseline,
        getTextAnchor,
        ...textStyleProps,

        // Primary label
        getTextColor: getTextColor as any,
        textSizeScale,
        textOutlineColor: textOutlineColor as Color,

        // TODO this still isn't working
        updateTriggers: { ...layerProps.updateTriggers, getText: [mainField?.name] }
      }
    }

    const collisionGroup = `${this.id}-labels`
    layerProps._subLayerProps = this.getSubLayerProps(
      {
        // linestrings & polygon-stroke
        featureTypes,
        opaOverwrite,
        // opacity: visConfig.opacity,
        // strokeOpacity: visConfig.strokeOpacity,

        // points-icon
        maxIconSize,

        // points-text
        extensions: defaultLayerProps.extensions,

        mainField,
        secondaryField,
        getSecondaryText,
        getSecondaryColor,
        secondarySizeScale,
        secondaryOutlineColor,
        radiusScale: layerProps.pointRadiusScale || 0.5 * (layerProps.iconSizeScale || 1)
      },
      ({
        featureTypes,
        opaOverwrite,
        maxIconSize,
        extensions,
        mainField,
        secondaryField,
        getSecondaryText,
        getSecondaryColor,
        secondarySizeScale,
        secondaryOutlineColor,
        radiusScale
      }) => ({
        ...(featureTypes?.linestring ? { linestrings: opaOverwrite } : {}),
        ...(featureTypes?.polygon ? { 'polygons-stroke': opaOverwrite } : {}),
        'points-icon': {
          loadOptions: {
            image: {
              type: 'imagebitmap'
            },
            imagebitmap: {
              resizeWidth: maxIconSize,
              resizeHeight: maxIconSize,
              resizeQuality: 'high'
            }
          }
        },
        'points-text': {
          type: PointLabelLayer,
          extensions: [...extensions, collisionFilterExtension],
          collisionEnabled: true,
          collisionGroup,

          ...(secondaryField && {
            getSecondaryText,
            getSecondaryColor,
            secondarySizeScale,
            secondaryOutlineColor
          }),
          radiusScale,
          updateTriggers: { getText: mainField?.name, getSecondaryText: secondaryField?.name }
        }
      })
    )

    const spatialIndex = geoColumn && getSpatialIndexFromGeoColumn(geoColumn)
    const tileSize = getTileSize({
      tileResolution: data.data.tileResolution,
      scheme: scheme as string,
      sourceType: sourceType as MapType,
      spatialIndex: spatialIndex as SpatialIndex
    })

    return {
      fetch: this.getCustomFetch(data.data as TilejsonResult),
      formatTiles,
      binary: true,
      ...defaultLayerProps,
      ...layerProps,
      ...data,
      data: data.data,
      opacity: this.useOpacityPerChannel ? 1 : visConfig.opacity,
      colorFormat: 'RGBA',
      pickable,
      highlightColor: this.config.highlightColor,
      autoHighlight: pickable,
      uniqueIdProperty: sourceType === MapType.tileset ? TILESETS_UNIQUE_ID_PROPERTY : DEFAULT_UNIQUE_ID_PROPERTY,
      wrapLongitude: true,
      lineMiterLimit: 2,
      rounded: true,
      tileSize,
      debounceTime: getDebounceTime(),
      onViewportLoad: (tiles: any) => this.onViewportLoad(tiles),
      loadOptions: cartoVectorTileLoadOptions
    }
  }

  onViewportLoad(tiles: any) {
    this.updateMeta({ onViewportLoad: true, tiles })
    if (this.isLoadingTiles) {
      getStore().dispatch(setIsLayerLoadingTiles({ layerId: this.id, isLoadingTiles: false }))
    }
    this.calculateFeatures()

    const firstContentIdFromTiles = getFirstContentIdFromTiles(tiles)
    if (firstContentIdFromTiles) {
      if (this.type === LayerTypes.Quadbin) {
        this.spatialIndexResolution = Number(getQuadbinResolution(firstContentIdFromTiles as bigint))
      } else if (this.type === LayerTypes.H3) {
        this.spatialIndexResolution = Number(getH3Resolution(firstContentIdFromTiles as string))
      }
    }
  }

  getSubLayerProps<PropT>(props: PropT, generator: (props: PropT) => any) {
    // deck compares props shallowly when diffing layers. To avoid recreating the TileLayer
    // cache the _subLayerProps if they are not changing
    const newCacheKey = JSON.stringify(props)
    if (newCacheKey !== this._subLayerPropsCacheKey) {
      this._subLayerPropsCacheKey = newCacheKey
      this._subLayerPropsCached = generator(props)
    }

    return this._subLayerPropsCached
  }

  renderLayer(opts: any): DeckGLLayer[] {
    if (!opts?.data?.data) return []
    const renderProps = this.getRenderProps(opts)

    const { highlightGeometry, highlightGeometryStyle } = this.config

    const isClick = highlightGeometryStyle !== 'hover'

    let highlightLayerProps: Partial<GeoJsonLayerProps>
    if (this.meta.featureTypes?.polygon) {
      highlightLayerProps = {
        getFillColor: isClick ? LAYER_HIGHLIGHT_FILL_COLOR_PRIMARY : LAYER_HIGHLIGHT_FILL_COLOR_HOVER,
        ...(isClick && {
          getLineColor: LAYER_HIGHLIGHT_LINE_COLOR_PRIMARY,
          getLineWidth: LAYER_HIGHLIGHT_LINE_WIDTH_MEDIUM
        })
      }
    } else if (this.meta.featureTypes?.line || this.meta.featureTypes?.linestring) {
      highlightLayerProps = {
        getLineColor: LAYER_HIGHLIGHT_LINE_COLOR_PRIMARY,
        getLineWidth: isClick ? LAYER_HIGHLIGHT_LINE_WIDTH_HEAVY : LAYER_HIGHLIGHT_LINE_WIDTH_LIGHT
      }
    } else {
      highlightLayerProps = {
        getIconColor: isClick ? LAYER_HIGHLIGHT_LINE_COLOR_PRIMARY : LAYER_HIGHLIGHT_FILL_COLOR_HOVER,
        getFillColor: isClick ? LAYER_HIGHLIGHT_FILL_COLOR_SECONDARY : LAYER_HIGHLIGHT_FILL_COLOR_HOVER,
        ...(isClick && {
          getLineColor: LAYER_HIGHLIGHT_LINE_COLOR_PRIMARY,
          getLineWidth: LAYER_HIGHLIGHT_LINE_WIDTH_HEAVY
        })
      }
    }

    return [
      new VectorTileLayer(renderProps),
      new GeoJsonLayer({
        ...renderProps,
        ...highlightLayerProps,
        id: `highlight-${renderProps.id}`,
        data: highlightGeometry,
        pickable: false,
        autoHighlight: false,
        opacity: 1,
        filled: true,
        stroked: true,
        lineWidthUnits: 'pixels',
        lineWidthScale: 1,
        // Labels shouldn't be highlighted
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        getText: () => null,
        _subLayerProps: null
      })
    ]
  }

  async getPickedFeatures({
    widgetSource,
    featureIds,
    columns,
    zoom,
    spatialFilter
  }: {
    widgetSource: WidgetSource
    featureIds: string[]
    columns: string[]
    zoom: number
    spatialFilter?: SpatialFilter
  }): Promise<Record<string, unknown>[]> {
    const spatialIndex = this.geoColumn ? getSpatialIndexFromGeoColumn(this.geoColumn) : undefined
    const tileResolution = getDynamicTileResolution(spatialIndex)

    let dataType: GeomType
    if (this.meta.featureTypes?.point) {
      dataType = GeomType.Points
    } else if (this.meta.featureTypes?.line || this.meta.featureTypes?.linestring) {
      dataType = GeomType.Lines
    } else if (this.meta.featureTypes?.polygon) {
      dataType = GeomType.Polygons
    } else {
      throw new Error('Cannot pick empty layer')
    }

    const { rows } = await widgetSource.getFeatures({
      columns,
      featureIds,
      dataType,
      spatialFilter,
      tileResolution,
      z: zoom
    })

    return rows
  }
}
